import { IconCloudOff, IconPencil, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Entry } from '@/types/api';
import { codeLabel, materialLabel, orderSurfaces } from '@/utils/dental';
import { PATHOLOGY_CODES } from '@/utils/entryKind';

type Props = {
  entry: Entry;
  showTooth?: boolean;
  /** Edit and delete; omitted for a read-only list. */
  actions?: {
    onEdit: () => void;
    onDelete: () => void;
    busy?: boolean;
  };
};

export function EntryTitle({ entry }: { entry: Entry }) {
  return (
    <>
      {codeLabel(entry.code)}
      {entry.surfaces.length > 0 && (
        <span className="mono"> {orderSurfaces(entry.surfaces).join('')}</span>
      )}
      {entry.material && <span> · {materialLabel(entry.material).toLowerCase()}</span>}
    </>
  );
}

/** existing (blue), performed in the visit (green), pathology awaiting treatment (red). */
export function entryTone(
  entry: Pick<Entry, 'kind' | 'code'>
): 'existing' | 'performed' | 'pathology' {
  if (PATHOLOGY_CODES.has(entry.code)) return 'pathology';
  return entry.kind === 'PERFORMED' ? 'performed' : 'existing';
}

/**
 * One entry of a visit. The doctor and time are shown once for the visit, not
 * here; the chip says whether it is existing state or performed work.
 */
export function EntryItem({ entry, showTooth = true, actions }: Props) {
  const { t } = useTranslation();
  const tone = entryTone(entry);
  return (
    <div className={`entry entry--${tone} ${entry.status === 'REJECTED' ? 'entry--rejected' : ''}`}>
      <div className="entry__top">
        {showTooth && <span className="tooth-pill">{entry.tooth}</span>}
        <div className="grow">
          <div className="entry__title">
            <EntryTitle entry={entry} />
          </div>
          {entry.note && <div className="small">{entry.note}</div>}
          <div className="entry__meta row">
            <span className={`kind-chip kind-chip--${tone}`}>{t(`chart.kind.${tone}`)}</span>
            {entry.local && (
              <span className="badge badge--offline">
                <IconCloudOff size={12} /> {t('chart.entry.notSynced')}
              </span>
            )}
            {entry.corrected && (
              <span className="badge badge--info">{t('chart.entry.corrected')}</span>
            )}
          </div>
        </div>
        {actions && entry.status !== 'REJECTED' && (
          <div className="entry__actions">
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              onClick={actions.onEdit}
              disabled={actions.busy || entry.local}
              aria-label={t('chart.entry.edit', { tooth: entry.tooth })}
              title={t('chart.entry.edit', { tooth: entry.tooth })}
            >
              <IconPencil size={18} />
            </button>
            <button
              type="button"
              className="icon-btn icon-btn--sm"
              onClick={actions.onDelete}
              disabled={actions.busy || entry.local}
              aria-label={t('chart.entry.delete', { tooth: entry.tooth })}
              title={t('chart.entry.delete', { tooth: entry.tooth })}
            >
              <IconTrash size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
